import { del, get, post, put } from '../request';
import instance from '../request';
import { toPage, type Page, type PageQuery } from './types';

export interface OutboundTask {
  id: number;
  name: string;
  type: string; // ai=AI 外呼 | agent=坐席外呼 | ivr=IVR 评分外呼
  status: string; // draft|queued|running|paused|done
  bot_code?: string;
  scenario_id?: number | null;
  scenario_name?: string; // v1.10 富化：外呼场景名（AI 型）
  concurrency?: number;
  total?: number;
  called?: number;
  connected?: number;
  gateway_id?: number | null;
  gateway_name?: string;
  caller_id_number?: string;
  dedup_scope?: string; // none|task|global
  // 富化
  created_by?: number;
  created_by_name?: string;
  skill_group_id?: number | null;
  skill_group_name?: string;
  bot_name?: string;
  remark?: string;
  started_at?: string;
  failed?: number;
  no_answer?: number;
  blacklisted?: number;
  retry_total?: number;
  /** 拨过但没接通的号码数（与结果明细「仅未接通」同口径，不含「接通但结局异常」的号码）：
   * 列表据此决定给不给「重拨未接通」入口 */
  unreached?: number;
  queue_pos?: number;
  // 重呼策略
  retry_max?: number;
  retry_interval_min?: number;
  call_window_start?: string; // "HH:mm"
  call_window_end?: string; // "HH:mm"
  daily_limit_per_number?: number;
  scheduled_at?: string;
  dial_rate?: number;
  call_interval_sec?: number;
  created_at?: string;
  [k: string]: any;
}

export interface OutboundCall {
  id: number;
  phone: string;
  status: string; // pending|calling|connected|failed|no_answer|blacklisted|skipped
  /** 关联通话记录（cdr）id：外呼明细只存 cdr_uuid，列表按 uuid 带出 cdr.id 供界面认号与打开通话详情；
   *  该明细尚无 CDR（未发起）时为 null */
  cdr_id?: number | null;
  /** FS 通道 uuid：外呼明细落库的关联键（by-cdr 反查与转写取数用它）；上面的 cdr_id 就是按它 join cdr 得来 */
  cdr_uuid?: string;
  fail_reason?: string;
  hangup_cause?: string;
  agent_id?: number | null;
  agent_name?: string;
  queue_wait_sec?: number | null;
  attempted_at?: string;
  attempts?: number;
  remark?: string;
  survey_result?: string;
  // v1.10 结果字段模板：result_values（确认结果 JSON）/ result_values_draft（AI 预填草稿）/
  // result_status（''|ai|draft|submitted）
  result_values?: string;
  result_values_draft?: string;
  result_status?: string;
  [k: string]: any;
}

/** 新建任务入参（UI 态）：botCode 取机器人 code，phones 为号码数组；重呼策略 camelCase */
export interface CreateTaskInput {
  name: string;
  type: string;
  botCode?: string;
  /** 外呼场景 id（营销/回访/催缴模型） */
  scenarioId?: number;
  concurrency: number;
  phones: string[];
  retryMax?: number;
  retryIntervalMin?: number;
  callWindowStart?: string; // "HH:mm"
  callWindowEnd?: string; // "HH:mm"
  dailyLimitPerNumber?: number;
  scheduledAt?: string;
  dialRate?: number;
  /** 任务内拨号间隔（秒）：0=不限制；所有在途呼叫结束后间隔该时长再拨下一通（防单路中继释放抖动） */
  callIntervalSec?: number;
  skillGroupId?: number;
  gatewayId?: number;
  callerIdNumber?: string;
  dedupScope?: string; // none|task|global
  remark?: string;
  autoStart?: boolean; // 是否开启任务：true=创建即排队（计划时间未到则排队等待）
}

/** 外呼场景 */
export interface OutboundScenario {
  id: number;
  code: string;
  scene_type: string; // marketing|return_visit|collection|notice
  name: string;
  bot_code?: string;
  opening?: string;
  main_flow?: string;
  closing?: string;
  /** 外呼使命/目标：本次外呼要达成的唯一目标（促成办理/留资/预约…），驱动 AI 主动推进 */
  goal?: string;
  target_intents?: string;
  result_tags?: string;
  transfer_rules?: string;
  sms_templates?: string;
  result_prompt?: string; // v1.10 结果提取补充指令
  /** 满意度评价是否回写工单：false=仅记录满意度与结果，不做工单动作（IVR/AI 满意度回访共用） */
  survey_ticket_writeback?: boolean;
  enabled: boolean;
}

/** v1.10 外呼结果字段模板（取值范围约束） */
export interface OutboundResultField {
  id: number;
  scenario_id: number; // 0=全局兜底模板
  field_key: string;
  label: string;
  field_type: string; // text|number|select|multi_select|date|boolean
  options?: string; // select 取值范围 JSON 数组字符串
  required?: boolean;
  sort?: number;
  enabled?: boolean;
  remark?: string;
}

/** GET /outbound-result-fields?scenarioId=（结果字段模板列表；0/空=全局模板） */
export async function listResultFields(scenarioId?: number): Promise<OutboundResultField[]> {
  const data: any = await get('/outbound-result-fields', { scenarioId: scenarioId ?? undefined });
  return data || [];
}

/** POST /outbound-result-fields */
export function createResultField(input: Partial<OutboundResultField>) {
  return post('/outbound-result-fields', input);
}

/** PUT /outbound-result-fields/:id */
export function updateResultField(id: number, input: Partial<OutboundResultField>) {
  return put(`/outbound-result-fields/${id}`, input);
}

/** DELETE /outbound-result-fields/:id */
export function deleteResultField(id: number) {
  return del(`/outbound-result-fields/${id}`);
}

/** PUT /outbound-tasks/:id/calls/:callId/result-values {values, status}：
 * 号码结果字段值写回（admin/supervisor 或接听坐席本人；submitted=坐席确认 / ai=覆盖 AI 结果） */
export function saveResultValues(taskId: number, callId: number, values: Record<string, string>, status: 'submitted' | 'ai') {
  return put(`/outbound-tasks/${taskId}/calls/${callId}/result-values`, { values, status });
}

/** v1.10 坐席工作台结果填写入口解析：CDR uuid → 外呼号码 + 任务 + 字段模板 */
export interface OutboundCallDetail {
  call_id: number;
  task_id: number;
  task_name: string;
  task_type: string; // ai|agent
  result_status?: string;
  result_values?: string;
  result_values_draft?: string;
  fields?: OutboundResultField[];
}

export async function getCallByCdr(uuid: string): Promise<OutboundCallDetail | null> {
  try {
    return await get(`/outbound-tasks/by-cdr/${uuid}`, undefined, { silent: true });
  } catch {
    return null; // 非外呼号码静默
  }
}

/** GET /outbound-scenarios */
export async function listScenarios(): Promise<OutboundScenario[]> {
  const data = await get('/outbound-scenarios', { page: 1, pageSize: 100 });
  return (data as any).list || [];
}

/** POST /outbound-scenarios（场景管理：Opening/MainFlow/Closing 话术） */
export function createScenario(input: Partial<OutboundScenario>) {
  return post('/outbound-scenarios', input);
}

/** PUT /outbound-scenarios/:id */
export function updateScenario(id: number, input: Partial<OutboundScenario>) {
  return put(`/outbound-scenarios/${id}`, input);
}

/** DELETE /outbound-scenarios/:id */
export function deleteScenario(id: number) {
  return del(`/outbound-scenarios/${id}`);
}

/** 引用某场景的外呼任务样本（删除前定位：编号 + 名称 + 状态） */
export interface ScenarioRefTask {
  id: number;
  name: string;
  type: string;
  status: string;
  created_at: string;
}

/** 场景的一类外部引用：任务类带 tasks 样本，其余带 samples 名称 */
export interface ScenarioRefItem {
  key: string;
  label: string;
  count: number;
  /** 硬引用=会阻断删除；软引用（结果字段模板）只提示 */
  hard: boolean;
  samples?: string[];
  tasks?: ScenarioRefTask[];
}

/** GET /outbound-scenarios/:id/refs 响应：blockers 非空即删除会被拦下 */
export interface ScenarioRefs {
  id: number;
  code: string;
  name: string;
  items: ScenarioRefItem[];
  blockers: string[];
}

/** GET /outbound-scenarios/:id/refs：删除前引用预览（与删除端护栏同源） */
export function scenarioRefs(id: number): Promise<ScenarioRefs> {
  return get(`/outbound-scenarios/${id}/refs`);
}

/** GET /outbound-tasks（silent=true 供运行中轮询，避免周期性失败 toast） */
export async function listTasks(q?: PageQuery & { name?: string; type?: string; status?: string }, silent = false): Promise<Page<OutboundTask>> {
  return toPage<OutboundTask>(await get('/outbound-tasks', { page: 1, pageSize: 50, ...(q || {}) }, silent ? { silent: true } : undefined));
}

/** GET /outbound-tasks/done-today：今日完成（接通）呼叫数，供首页统计卡 */
export async function fetchOutboundDoneToday(): Promise<number> {
  const d = (await get('/outbound-tasks/done-today')) as { done_today?: number };
  return d?.done_today ?? 0;
}

/** GET /outbound-tasks/:id（深链兜底） */
export async function getTask(id: number): Promise<OutboundTask> {
  return get(`/outbound-tasks/${id}`);
}

/** GET /outbound-tasks/:id/phones：任务全部号码（复制任务预填） */
export async function listTaskPhones(id: number): Promise<string[]> {
  const data: any = await get(`/outbound-tasks/${id}/phones`);
  return data?.phones || [];
}

/** POST /outbound-tasks —— { name, type, bot_code, concurrency, phones, 重呼策略字段, skill_group_id, remark } */
/** POST /outbound-tasks/callback —— 工作台预约回呼（LoginOnly，不走外呼任务页权限） */
export function createWorkbenchCallback(input: { phone: string; scheduledAt: string; remark?: string }) {
  return post('/outbound-tasks/callback', {
    phone: input.phone,
    scheduled_at: input.scheduledAt,
    remark: input.remark,
  });
}

export function createTask(input: CreateTaskInput) {
  const payload: Record<string, any> = {
    name: input.name,
    type: input.type,
    concurrency: input.concurrency,
    phones: input.phones,
  };
  if (input.botCode) payload.bot_code = input.botCode;
  if (input.scenarioId) payload.scenario_id = input.scenarioId;
  if (input.retryMax !== undefined && input.retryMax !== null) payload.retry_max = input.retryMax;
  if (input.retryIntervalMin !== undefined && input.retryIntervalMin !== null) payload.retry_interval_min = input.retryIntervalMin;
  if (input.callWindowStart) payload.call_window_start = input.callWindowStart;
  if (input.callWindowEnd) payload.call_window_end = input.callWindowEnd;
  if (input.dailyLimitPerNumber !== undefined && input.dailyLimitPerNumber !== null) {
    payload.daily_limit_per_number = input.dailyLimitPerNumber;
  }
  if (input.scheduledAt) payload.scheduled_at = input.scheduledAt;
  if (input.dialRate !== undefined && input.dialRate !== null) payload.dial_rate = input.dialRate;
  if (input.callIntervalSec !== undefined && input.callIntervalSec !== null) {
    payload.call_interval_sec = input.callIntervalSec;
  }
  if (input.skillGroupId) payload.skill_group_id = input.skillGroupId;
  if (input.gatewayId) payload.gateway_id = input.gatewayId;
  if (input.callerIdNumber) payload.caller_id_number = input.callerIdNumber;
  if (input.dedupScope) payload.dedup_scope = input.dedupScope;
  if (input.remark) payload.remark = input.remark;
  if (input.autoStart !== undefined) payload.auto_start = input.autoStart;
  return post('/outbound-tasks', payload);
}

export function startTask(id: number) {
  return post(`/outbound-tasks/${id}/start`);
}

export function stopTask(id: number) {
  return post(`/outbound-tasks/${id}/stop`);
}

/** POST /outbound-tasks/:id/retry-unreached：任务级重拨未接通（号码置回待呼叫 + 任务重新排队）。
 * 返回重置数、因当日呼叫上限未重置的号码数与操作后的任务状态；无可重拨号码时接口报错。 */
export interface RetryUnreachedResult {
  reset: number;
  blocked_daily_limit: number;
  status: string;
}

export function retryUnreachedTask(id: number): Promise<RetryUnreachedResult> {
  return post(`/outbound-tasks/${id}/retry-unreached`);
}

/** POST /outbound-tasks/:id/resume：暂停任务重新排队 */
export function resumeTask(id: number) {
  return post(`/outbound-tasks/${id}/resume`);
}

/** DELETE /outbound-tasks/:id：删除草稿任务（排队中先取消排队，运行中/暂停先停止拨号） */
export function deleteTask(id: number) {
  return del(`/outbound-tasks/${id}`);
}

/** PUT /outbound-tasks/:id：编辑草稿任务（仅 draft 可编辑，后端守卫） */
export function updateTask(id: number, input: Partial<CreateTaskInput>) {
  const payload: Record<string, any> = {};
  if (input.name !== undefined) payload.name = input.name;
  if (input.botCode !== undefined && input.botCode !== null) payload.bot_code = input.botCode;
  if (input.scenarioId !== undefined) payload.scenario_id = input.scenarioId ?? null;
  if (input.concurrency !== undefined && input.concurrency !== null) payload.concurrency = input.concurrency;
  if (input.retryMax !== undefined && input.retryMax !== null) payload.retry_max = input.retryMax;
  if (input.retryIntervalMin !== undefined && input.retryIntervalMin !== null) payload.retry_interval_min = input.retryIntervalMin;
  if (input.callWindowStart !== undefined) payload.call_window_start = input.callWindowStart;
  if (input.callWindowEnd !== undefined) payload.call_window_end = input.callWindowEnd;
  if (input.dailyLimitPerNumber !== undefined && input.dailyLimitPerNumber !== null) {
    payload.daily_limit_per_number = input.dailyLimitPerNumber;
  }
  if (input.scheduledAt !== undefined && input.scheduledAt !== null) payload.scheduled_at = input.scheduledAt;
  if (input.dialRate !== undefined && input.dialRate !== null) payload.dial_rate = input.dialRate;
  if (input.callIntervalSec !== undefined && input.callIntervalSec !== null) {
    payload.call_interval_sec = input.callIntervalSec;
  }
  if (input.skillGroupId !== undefined) payload.skill_group_id = input.skillGroupId ?? null;
  if (input.gatewayId !== undefined) payload.gateway_id = input.gatewayId ?? null;
  if (input.callerIdNumber !== undefined) payload.caller_id_number = input.callerIdNumber;
  if (input.dedupScope !== undefined) payload.dedup_scope = input.dedupScope;
  if (input.remark !== undefined) payload.remark = input.remark;
  if (input.autoStart !== undefined) payload.auto_start = input.autoStart;
  return put(`/outbound-tasks/${id}`, payload);
}

/** PUT /outbound-tasks/:id/runtime：运行中调速/调并发/调拨号间隔 */
export function runtimeUpdateTask(id: number, p: { concurrency?: number; dial_rate?: number; call_interval_sec?: number }) {
  return put(`/outbound-tasks/${id}/runtime`, p);
}

/** 重呼失败号码 / 拉黑（拉黑自动进全局呼入黑名单） */
export function recallCall(taskId: number, callId: number) {
  return post(`/outbound-tasks/${taskId}/calls/${callId}/recall`, {});
}
export function blacklistCall(taskId: number, callId: number) {
  return post(`/outbound-tasks/${taskId}/calls/${callId}/blacklist`, {});
}

/** 号码备注 / 调查结果 */
export function updateCallRemark(taskId: number, callId: number, remark: string) {
  return put(`/outbound-tasks/${taskId}/calls/${callId}/remark`, { remark });
}
export function saveSurveyResult(taskId: number, callId: number, result: string) {
  return put(`/outbound-tasks/${taskId}/calls/${callId}/survey`, { result });
}

/** GET /outbound-tasks/:id/calls（status/fail_reason 过滤） */
export async function listTaskCalls(
  id: number,
  q?: PageQuery & { fail_reason?: string; status?: string }
): Promise<Page<OutboundCall>> {
  return toPage<OutboundCall>(await get(`/outbound-tasks/${id}/calls`, { page: 1, pageSize: 200, ...(q || {}) }, { silent: true }));
}

// ---------- 结果明细（逐通外呼结果收口视图） ----------

/** 结果明细汇总（任务号码级口径，不受分页与列表筛选影响） */
export interface OutboundResultsSummary {
  total: number; // 任务号码总数
  reached: number; // 拨通（含 AI 会话结局异常但仍接通的号码）
  with_value: number; // 拨通且已落结果
  no_value: number; // 拨通但无结果（需人工兜底）
  /** 拨过但没接通（占线/无应答/呼叫失败等）；与 reached 互补，两档合起来是「已拨打过」 */
  unreached: number;
  /** 未接通里的「占线」子集：暂时性失败，据此判断要不要重拨 */
  busy: number;
  /** 结果标签分布（仅拨通号码）；tag 为空串表示未分类 */
  tags?: { tag: string; count: number }[];
}

/** 列表内再收窄：all=全部拨通 | unreached=仅未接通 | value=仅已落结果 | empty=仅未落结果 */
export type OutboundResultsOnly = 'all' | 'unreached' | 'value' | 'empty';

/** GET /outbound-tasks/:id/results 响应：汇总 + 生效结果字段模板 + 拨通号码分页行 */
export interface OutboundResults {
  task?: OutboundTask;
  summary?: OutboundResultsSummary;
  /** 任务实际生效的结果字段模板（场景专属 → 全局兜底）；动态列按此展开 */
  fields?: OutboundResultField[];
  list?: OutboundCall[];
  /** 当前筛选下的行数（分页用）：only=unreached 时数的是未接通行，其余档是拨通行 */
  total?: number;
}

/** GET /outbound-tasks/:id/results：结果明细分页取数（keyword 按号码模糊匹配） */
export function listTaskResults(
  id: number,
  q?: PageQuery & { keyword?: string; only?: OutboundResultsOnly; tag?: string }
): Promise<OutboundResults> {
  return get(`/outbound-tasks/${id}/results`, { page: 1, pageSize: 50, ...(q || {}) });
}

/** GET /outbound-tasks/import-template：号码导入模板（xlsx 下载） */
export function getImportTemplateUrl() {
  return '/outbound-tasks/import-template';
}

/** POST /outbound-tasks/:id/phones/import：CSV 上传导入号码（multipart） */
export async function importPhones(id: number, file: File): Promise<{ ok_rows: number; dup_rows: number; invalid_rows: number; errors: { row: number; phone: string; reason: string }[] }> {
  const fd = new FormData();
  fd.append('file', file);
  const data: any = await instance.post(`/outbound-tasks/${id}/phones/import`, fd, { timeout: 60000 });
  return data;
}

// ---------- 任务看板 ----------

/** 工单摘要（来源工单/回执工单展示） */
export interface TicketBrief {
  id: number;
  ticket_no?: string;
  title?: string;
  status?: string;
  [k: string]: any;
}

/** 任务看板（后端形状：任务字段嵌套在 task 下，前端 Board 兼容读取） */
export interface OutboundBoard {
  task?: OutboundTask;
  progress?: number; // 0-1 或 0-100，展示层归一
  connect_rate?: number; // 0-1 或 0-100，展示层归一
  results?: {
    connected?: number;
    no_answer?: number;
    failed?: number;
    pending?: number;
    calling?: number;
    blacklisted?: number;
    skipped?: number;
  };
  created_by_name?: string;
  skill_group_name?: string;
  /** 坐席门控中：技能组无空闲坐席暂停拨号 */
  waiting_agent?: boolean;
  /** 来源工单（工单回访任务） */
  source_ticket?: TicketBrief | null;
  /** 回执工单（AI 自动建单：任务呼叫产生的工单） */
  result_tickets?: TicketBrief[];
  [k: string]: any;
}

/** GET /outbound-tasks/:id/board（30s 轮询用，静默失败） */
export function getTaskBoard(id: number): Promise<OutboundBoard> {
  return get(`/outbound-tasks/${id}/board`, undefined, { silent: true });
}
